// ============================================================================
// note-client-mod -- NoteApiClient
//
// HTTP API 客户端，封装对 /api/notes/*、/api/notebooks/* 和公开接口的调用。
//
// 支持两种使用方式：
// 1. 通过 IFetchService（由 DI 注入，浏览器/SSR 环境自动适配）
// 2. 直接使用（向后兼容，默认使用原生 fetch）
// ============================================================================
/* -------------------------------------------------------------------------- */
/* 工具函数                                                                    */
/* -------------------------------------------------------------------------- */
/** 获取 auth token（从 localStorage） */
function getAuthToken() {
    if (typeof window === 'undefined')
        return null;
    return localStorage.getItem('accessToken');
}
/* -------------------------------------------------------------------------- */
/* NoteApiClient                                                              */
/* -------------------------------------------------------------------------- */
export class NoteApiClient {
    constructor(deps) {
        if (deps && typeof deps === 'object' && 'fetchService' in deps) {
            this.fetchService = deps.fetchService ?? null;
        }
        else {
            this.fetchService = deps ?? null;
        }
    }
    /** 替换内部 fetch 服务（DI 容器加载时调用） */
    setFetchService(fetchService) {
        this.fetchService = fetchService;
    }
    /** 获取当前 fetch 服务 */
    getFetchService() {
        return this.fetchService;
    }
    async doFetch(url, options) {
        if (this.fetchService) {
            return this.fetchService.fetch(url, options);
        }
        const response = await globalThis.fetch(url, {
            method: options?.method ?? 'GET',
            headers: options?.headers,
            body: options?.body,
        });
        return {
            status: response.status,
            ok: response.ok,
            json: () => response.json(),
            text: () => response.text(),
        };
    }
    get authHeaders() {
        const token = getAuthToken();
        return token ? { Authorization: `Bearer ${token}` } : {};
    }
    buildQuery(params) {
        if (!params)
            return '';
        const searchParams = new URLSearchParams();
        if (params.notebookId)
            searchParams.set('notebookId', params.notebookId);
        if (params.visibility)
            searchParams.set('visibility', params.visibility);
        if (params.tag)
            searchParams.set('tag', params.tag);
        if (params.isPinned !== undefined)
            searchParams.set('isPinned', String(params.isPinned));
        if (params.isArchived !== undefined)
            searchParams.set('isArchived', String(params.isArchived));
        if (params.isFavorite !== undefined)
            searchParams.set('isFavorite', String(params.isFavorite));
        if (params.keyword)
            searchParams.set('keyword', params.keyword);
        if (params.page)
            searchParams.set('page', String(params.page));
        if (params.pageSize)
            searchParams.set('pageSize', String(params.pageSize));
        if (params.sortBy)
            searchParams.set('sortBy', params.sortBy);
        if (params.sortOrder)
            searchParams.set('sortOrder', params.sortOrder);
        const qs = searchParams.toString();
        return qs ? `?${qs}` : '';
    }
    async request(url, method, body) {
        const response = await this.doFetch(url, {
            method,
            headers: {
                'Content-Type': 'application/json',
                ...this.authHeaders,
            },
            body: body !== undefined ? JSON.stringify(body) : undefined,
        });
        if (!response.ok) {
            let message = `Request failed: ${response.status}`;
            try {
                const data = await response.json();
                if (data && typeof data === 'object' && 'error' in data) {
                    message = data.error;
                }
            }
            catch { /* ignore */ }
            throw new Error(message);
        }
        return response.json();
    }
    // === 认证 Note API ===
    async getNotes(params) {
        return this.request(`/api/notes${this.buildQuery(params)}`, 'GET');
    }
    async getNote(id) {
        return this.request(`/api/notes/${id}`, 'GET');
    }
    async createNote(params) {
        return this.request('/api/notes', 'POST', params);
    }
    async updateNote(id, params) {
        return this.request(`/api/notes/${id}`, 'PATCH', params);
    }
    async deleteNote(id) {
        await this.request(`/api/notes/${id}`, 'DELETE');
    }
    async togglePin(id) {
        return this.request(`/api/notes/${id}/toggle-pin`, 'POST');
    }
    async toggleArchive(id) {
        return this.request(`/api/notes/${id}/toggle-archive`, 'POST');
    }
    async toggleFavorite(id) {
        return this.request(`/api/notes/${id}/toggle-favorite`, 'POST');
    }
    async getStats() {
        return this.request('/api/notes/stats', 'GET');
    }
    // === 公开 Note API（无需认证） ===
    async getPublicNotes(params) {
        const query = this.buildQuery(params);
        return this.request(`/api/public/notes${query}`, 'GET');
    }
    async getPublicNote(id) {
        return this.request(`/api/public/notes/${id}`, 'GET');
    }
    // === Notebook API ===
    async getNotebooks() {
        return this.request('/api/notebooks', 'GET');
    }
    async getNotebook(id) {
        return this.request(`/api/notebooks/${id}`, 'GET');
    }
    async createNotebook(params) {
        return this.request('/api/notebooks', 'POST', params);
    }
    async updateNotebook(id, params) {
        return this.request(`/api/notebooks/${id}`, 'PATCH', params);
    }
    async deleteNotebook(id) {
        await this.request(`/api/notebooks/${id}`, 'DELETE');
    }
    async getNotebookStats() {
        return this.request('/api/notebooks/stats', 'GET');
    }
}
/* -------------------------------------------------------------------------- */
/* 单例管理                                                                    */
/* -------------------------------------------------------------------------- */
/** 全局单例（默认使用原生 fetch） */
export const noteApiClient = new NoteApiClient();
/** 设置全局单例（DI 容器加载时替换为注入 IFetchService 的实例） */
export function setNoteApiClient(client) {
    noteApiClient.setFetchService(client.getFetchService());
}
/** 获取全局单例 */
export function getNoteApiClient() {
    return noteApiClient;
}
//# sourceMappingURL=note-api-client.js.map